import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { dashSummaryAtom } from '../atoms-types/dashSummary';

interface PowerSumGraphProps {
  type: 'solarLoad' | 'importExport';
}

const PowerSumGraph: React.FC<PowerSumGraphProps> = ({ type }) => {
  const theme = useTheme();
  const dashSummary = useAtomValue(dashSummaryAtom);
  
  // Get values based on type
  const values = type === 'solarLoad' 
    ? [
        { name: 'Load', value: dashSummary.powerSum.totalLoadKwh, color: theme.palette.success.main },
        { name: 'Solar', value: dashSummary.powerSum.totalSolarKwh, color: theme.palette.warning.main }
      ]
    : [
        { name: 'Import', value: dashSummary.powerSum.totalImportedKwh, color: theme.palette.error.main },
        { name: 'Export', value: dashSummary.powerSum.totalExportedKwh, color: theme.palette.info.main }
      ];

  const option = {
    grid: {
      left: '35%',
      right: '10%',
      top: '15%',
      bottom: '10%',
      containLabel: true
    },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      formatter: function(params: any) {
        let result = '';
        // Show both values regardless of which bar is hovered
        values.forEach((value) => {
          result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${value.color};"></span>${value.name}: ${value.value.toFixed(2)} kWh<br/>`;
        });
        result += `<span style="color:gray;font-size:12px">Total over last 24hrs</span>`;
        return result;
      }
    },
    xAxis: [
      {
        type: 'category',
        data: ['', ''],
        axisPointer: {
          type: 'shadow'
        },
        axisLabel: {
          show: false
        },
        boundaryGap: true,
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        name: type === 'solarLoad' ? 'Load / Solar' : 'Import / Export',
        nameLocation: 'middle',
        nameGap: 70,
        nameTextStyle: {
          color: theme.palette.text.primary,
          fontSize: 20,
          fontWeight: 'bold'
        },
        axisLabel: {
          formatter: '{value} kWh',
          color: theme.palette.text.secondary,
          margin: 10,
          padding: [0, 0, 0, 16]
        }
      }
    ],
    series: [
      {
        type: 'bar',
        data: values.map((v, index) => ({
          value: v.value,
          itemStyle: {
            color: v.color
          },
          label: {
            show: true,
            position: 'bottom',
            formatter: v.value.toFixed(2),
            fontSize: 15,
            color: theme.palette.text.primary,
            fontWeight: 'bold'
          }
        })),
        barWidth: '70%',
        showBackground: true,
        backgroundStyle: {
          color: 'rgba(180, 180, 180, 0.2)'
        }
      }
    ]
  };

  return (
    <ReactECharts 
      key={`${type}-${values.map(v => v.value).join('-')}`}
      option={option} 
      className="cancelSelectorName" 
      style={{ height: '300px', width: '100%' }} 
      opts={{ renderer: 'canvas' }}
      notMerge={false}
      lazyUpdate={true}
    />
  );
};

export default PowerSumGraph;
